import React, { useState, useEffect, useMemo } from 'react';
import { compatApi } from '../../utils/Intercepter';
import { useFilteredPagination } from '../../utils/PageUtil';
import { format } from 'date-fns';
import DeleteOrganizationConfirmModal from '../../components/deleteOrganizationConfirmModal';
import OrganizationInfoModal from '../../components/OrganizationInfoModal';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';
import { useIsMobile } from '../../utils/responsive';

// ─── Sub-components ──────────────────────────────────────────────────────────

function OrgStatusBadge({ status }) {
    const map = {
        'Y': { label: '승인완료', color: MINT, bg: hexToRgba(MINT, .14), dot: MINT },
        'N': { label: '대기', color: '#d97757', bg: 'rgba(217,119,87,.14)', dot: '#d97757' },
        'R': { label: '반려', color: '#c44569', bg: 'rgba(196,69,105,.12)', dot: '#c44569' },
    };
    const s = map[status] || { label: status, color: '#5d5b6e', bg: '#f4f3f9', dot: '#aaa9b7' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 11.5, fontWeight: 600, color: s.color,
            background: s.bg, padding: '3px 9px', borderRadius: 999, lineHeight: 1.5,
            whiteSpace: 'nowrap',
        }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />
            {s.label}
        </span>
    );
}

function StatTile({ label, value, suffix, accent, icon, highlight }) {
    return (
        <div style={{
            background: 'white', borderRadius: 16, padding: '16px 18px',
            border: highlight ? `1.5px solid ${hexToRgba(accent, .35)}` : '1px solid #f0eff5',
            boxShadow: '0 1px 2px rgba(60,50,120,.04)',
            display: 'flex', alignItems: 'center', gap: 14,
        }}>
            <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: hexToRgba(accent, .12), color: accent,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>{icon}</div>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
                <span style={{ fontSize: 11.5, color: '#8b8a99', fontWeight: 500, letterSpacing: '.03em' }}>{label}</span>
                <span style={{ fontSize: 22, fontWeight: 700, color: '#1f1d2e', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                    {value}
                    <span style={{ fontSize: 12, color: '#8b8a99', fontWeight: 500, marginLeft: 3 }}>{suffix}</span>
                </span>
            </div>
        </div>
    );
}

function PillGroup({ label, value, options, onChange }) {
    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: '#8b8a99', fontWeight: 600, letterSpacing: '.04em' }}>{label}</span>
            <div style={{ display: 'inline-flex', gap: 4, background: '#f4f3f9', padding: 3, borderRadius: 10 }}>
                {options.map((opt) => {
                    const active = opt === value;
                    return (
                        <button key={opt} onClick={() => onChange(opt)} style={{
                            height: 28, padding: '0 12px', borderRadius: 7, border: 'none',
                            background: active ? 'white' : 'transparent',
                            color: active ? '#1f1d2e' : '#8b8a99',
                            fontSize: 12, fontWeight: active ? 600 : 500,
                            cursor: active ? 'default' : 'pointer', fontFamily: 'inherit',
                            boxShadow: active ? '0 1px 3px rgba(60,50,120,.10)' : 'none',
                            transition: 'all 140ms', whiteSpace: 'nowrap',
                        }}>{opt}</button>
                    );
                })}
            </div>
        </div>
    );
}

const thStyle = (align = 'center', sortable = false) => ({
    padding: '12px 12px',
    fontSize: 12, fontWeight: 600, color: '#5d5b6e',
    textAlign: align,
    borderBottom: '1px solid #ececf2',
    background: '#f7f6fb',
    whiteSpace: 'nowrap',
    letterSpacing: '.02em',
    cursor: sortable ? 'pointer' : 'default',
    userSelect: 'none',
});

const tdStyle = (align = 'left', muted = false) => ({
    padding: '14px 12px',
    textAlign: align,
    borderBottom: '1px solid #f4f3f9',
    fontSize: 12.5,
    color: muted ? '#5d5b6e' : '#3a3849',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
});

const IconChevron = ({ width = 11, height = 11, style = {} }) => (
    <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <path d="m9 18 6-6-6-6" />
    </svg>
);

function pageBtn(disabled, active = false) {
    return {
        minWidth: 32, height: 32, borderRadius: 8,
        border: active ? 'none' : '1.5px solid #ececf2',
        background: active ? `linear-gradient(135deg, ${PURPLE}, ${MINT})` : disabled ? '#fafaff' : 'white',
        color: active ? 'white' : disabled ? '#c7c5d2' : '#3a3849',
        fontSize: 13, fontWeight: active ? 600 : 500, cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit',
    };
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PendingAppr() {
    const isMobile = useIsMobile(768);
    const [originalOrgList, setOriginalOrgList] = useState([]);
    const [statusFilter, setStatusFilter] = useState('전체');
    const [showModal, setShowModal] = useState(false);
    const [showOrgInfoModal, setShowOrgInfoModal] = useState(false);
    const [organizationCodeToDelete, setOrganizationCodeToDelete] = useState(null);
    const [selectedOrgData, setSelectedOrgData] = useState(null);

    const filterMap = { '승인완료': 'Y', '대기': 'N', '반려': 'R' };

    const filteredList = useMemo(() => {
        if (statusFilter === '전체') return originalOrgList;
        const code = filterMap[statusFilter];
        return originalOrgList.filter(o => o.isApproved === code);
    }, [originalOrgList, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const {
        currentItems,
        currentPage,
        totalPages,
        handlePageChange,
        handleNextGroup,
        handlePrevGroup,
        applySort,
        sortConfig,
        updateOriginalData,
    } = useFilteredPagination(filteredList, null, 10, 5, { key: 'registrationDate', direction: 'desc' });

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    const counts = useMemo(() => {
        const c = { total: originalOrgList.length, approved: 0, pending: 0, rejected: 0 };
        originalOrgList.forEach(o => {
            if (o.isApproved === 'Y') c.approved++;
            else if (o.isApproved === 'N') c.pending++;
            else if (o.isApproved === 'R') c.rejected++;
        });
        return c;
    }, [originalOrgList]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await compatApi.get('/org/apprOrgList');
                if (response.ok) {
                    setOriginalOrgList(response.data);
                    updateOriginalData(response.data);
                } else {
                    alert('조직 목록을 가져오는 데 실패했습니다.');
                }
            } catch (error) {
                alert('오류가 발생했습니다.');
            }
        };
        fetchData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleApprove = async (organizationCode) => {
        try {
            const response = await compatApi.post('/org/approve', organizationCode);
            if (response.ok) {
                alert('승인되었습니다.');
                const updated = originalOrgList.map(item =>
                    item.organizationCode === organizationCode
                        ? { ...item, isApproved: 'Y', approvalDate: format(new Date(), 'yyyy-MM-dd') }
                        : item
                );
                setOriginalOrgList(updated);
                updateOriginalData(updated);
            } else {
                alert('승인 중 오류가 발생했습니다.');
            }
        } catch {
            alert('네트워크 오류가 발생했습니다.');
        }
    };

    const handleReject = async (organizationCode) => {
        try {
            const response = await compatApi.post('/org/reject', organizationCode);
            if (response.ok) {
                alert('반려되었습니다.');
                const updated = originalOrgList.map(item =>
                    item.organizationCode === organizationCode
                        ? { ...item, isApproved: 'R', approvalDate: null }
                        : item
                );
                setOriginalOrgList(updated);
                updateOriginalData(updated);
            } else {
                alert('반려 중 오류가 발생했습니다.');
            }
        } catch {
            alert('네트워크 오류가 발생했습니다.');
        }
    };

    const handleDelete = async (organizationCode) => {
        setOrganizationCodeToDelete(organizationCode);
        if (!window.confirm('정말로 삭제하시겠습니까?\n삭제된 기업 정보는 복구되지 않습니다.')) return;
        try {
            const response = await compatApi.del('/org/delete', organizationCode);
            const message = typeof response.data === 'string' ? response.data : '';
            if (response.ok) {
                alert('삭제가 정상적으로 처리되었습니다.');
                const updated = originalOrgList.filter(item => item.organizationCode !== organizationCode);
                setOriginalOrgList(updated);
                updateOriginalData(updated);
            } else {
                if (message === 'DataIntegrityViolationException') {
                    setShowModal(true);
                } else {
                    alert(message || '삭제 중 오류가 발생했습니다.');
                }
            }
        } catch (error) {
            if (error.response?.data === 'DataIntegrityViolationException') {
                setShowModal(true);
            } else {
                alert('네트워크 오류가 발생했습니다.');
            }
        }
    };

    const handleCancel = async (action, organizationCode) => {
        const label = action === 'cancel' ? '승인취소' : '롤백';
        if (!window.confirm(`${label} 하시겠습니까?`)) return;
        try {
            const response = await compatApi.post('/org/reset', organizationCode);
            if (response.ok) {
                alert(`${label}되었습니다.`);
                const updated = originalOrgList.map(item =>
                    item.organizationCode === organizationCode
                        ? { ...item, isApproved: 'N', approvalDate: null }
                        : item
                );
                setOriginalOrgList(updated);
                updateOriginalData(updated);
            } else {
                alert(`${label} 중 오류가 발생했습니다.`);
            }
        } catch {
            alert('네트워크 오류가 발생했습니다.');
        }
    };

    const handleForceDelete = async (organizationCode) => {
        try {
            const response = await compatApi.del('/org/delete-with-users', organizationCode);
            const message = typeof response.data === 'string' ? response.data : '';
            if (response.ok) {
                alert('삭제가 정상적으로 처리되었습니다.');
                const updated = originalOrgList.filter(item => item.organizationCode !== organizationCode);
                setOriginalOrgList(updated);
                updateOriginalData(updated);
            } else {
                alert(message || '삭제 중 오류가 발생했습니다.');
            }
        } catch {
            alert('네트워크 오류가 발생했습니다.');
        }
    };

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
            <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>

                {/* 헤더 */}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
                        <span>관리자</span>
                        <IconChevron style={{ opacity: 0.6 }} />
                        <span style={{ color: '#3a3849', fontWeight: 500 }}>조직 관리</span>
                    </div>
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>조직 관리</h1>
                    <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#7a7989' }}>
                        전체 조직을 조회하고 가입 승인·반려를 처리합니다.
                    </p>
                </div>

                {/* 통계 타일 */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 14, marginBottom: 16 }}>
                    <StatTile label="전체 조직" value={counts.total} suffix="개" accent={PURPLE}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>}
                    />
                    <StatTile label="승인 완료" value={counts.approved} suffix="개" accent={MINT}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4 12 14.01l-3-3" /></svg>}
                    />
                    <StatTile label="승인 대기" value={counts.pending} suffix="개" accent="#d97757"
                        highlight={counts.pending > 0}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>}
                    />
                    <StatTile label="반려" value={counts.rejected} suffix="개" accent="#c44569"
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="m15 9-6 6M9 9l6 6" /></svg>}
                    />
                </div>

                {/* 필터 행 */}
                <div style={{
                    background: 'white', borderRadius: 16, padding: '16px 20px',
                    border: '1px solid #f0eff5', boxShadow: '0 1px 2px rgba(60,50,120,.04)',
                    marginBottom: 16,
                }}>
                    <PillGroup
                        label="상태"
                        value={statusFilter}
                        options={['전체', '승인완료', '대기', '반려']}
                        onChange={(v) => { setStatusFilter(v); handlePageChange(1); }}
                    />
                </div>

                {/* 테이블 카드 */}
                <div style={{
                    background: 'white', borderRadius: 16,
                    border: '1px solid #f0eff5',
                    boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                    overflow: 'hidden',
                }}>
                    {/* 카드 헤더 */}
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '16px 20px',
                        background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)',
                        borderBottom: '1px solid #ececf2',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>조직 목록</h2>
                            <span style={{
                                fontSize: 11, fontWeight: 600,
                                background: 'white', color: '#5d5b6e',
                                padding: '2px 8px', borderRadius: 999, lineHeight: 1.45,
                                border: '1px solid #ececf2',
                            }}>총 {filteredList.length}건</span>
                        </div>
                    </div>

                    {/* 테이블 */}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%', borderCollapse: 'separate', borderSpacing: 0,
                            fontSize: 13, color: '#3a3849',
                        }}>
                            <thead>
                                <tr>
                                    <th style={thStyle('center')}>번호</th>
                                    <th style={thStyle('left')}>조직 명</th>
                                    <th style={thStyle('left')}>조직 코드</th>
                                    <th style={thStyle('left')}>사업자 번호</th>
                                    <th style={thStyle('left')}>주소</th>
                                    <th style={thStyle('center', true)} onClick={() => applySort('registrationDate')}>
                                        조직 생성일{getSortIcon('registrationDate')}
                                    </th>
                                    <th style={thStyle('center')}>조직 승인일</th>
                                    <th style={thStyle('center')}>승인 여부</th>
                                    <th style={thStyle('center')}>액션</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map((org, idx) => (
                                    <tr key={org.organizationCode}
                                        style={{ background: 'white', transition: 'background 100ms' }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#fafaff'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                    >
                                        <td style={tdStyle('center', true)}>
                                            <span style={{ color: '#8b8a99' }}>{(currentPage - 1) * 10 + idx + 1}</span>
                                        </td>
                                        <td style={tdStyle('left')}>
                                            <button onClick={() => { setSelectedOrgData(org); setShowOrgInfoModal(true); }} style={{
                                                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                                color: PURPLE, fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                                                textDecoration: 'underline', textUnderlineOffset: 2,
                                            }}>{org.organizationName}</button>
                                        </td>
                                        <td style={tdStyle('left', true)}>
                                            <span style={{
                                                fontSize: 11.5, color: '#5d5b6e',
                                                background: '#f4f3f9', padding: '2px 8px', borderRadius: 6, fontFamily: 'monospace',
                                            }}>{org.organizationCode}</span>
                                        </td>
                                        <td style={tdStyle('left', true)}>{org.bizRegNo || <span style={{ color: '#c7c5d2' }}>—</span>}</td>
                                        <td style={{ ...tdStyle('left', true), maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {org.address || <span style={{ color: '#c7c5d2' }}>—</span>}
                                        </td>
                                        <td style={tdStyle('center', true)}>{org.registrationDate || <span style={{ color: '#c7c5d2' }}>—</span>}</td>
                                        <td style={tdStyle('center', true)}>
                                            {org.approvalDate
                                                ? <span style={{ color: MINT, fontWeight: 600 }}>{org.approvalDate}</span>
                                                : <span style={{ color: '#c7c5d2' }}>—</span>
                                            }
                                        </td>
                                        <td style={tdStyle('center')}>
                                            <OrgStatusBadge status={org.isApproved} />
                                        </td>
                                        <td style={tdStyle('center')}>
                                            <div style={{ display: 'inline-flex', gap: 4, justifyContent: 'center' }}>
                                                {org.isApproved === 'N' && (
                                                    <>
                                                        <button onClick={() => handleApprove(org.organizationCode)} style={{
                                                            padding: '4px 10px', borderRadius: 6, border: 'none',
                                                            background: '#dcfce7', color: '#15803d',
                                                            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                                        }}>승인</button>
                                                        <button onClick={() => handleReject(org.organizationCode)} style={{
                                                            padding: '4px 10px', borderRadius: 6, border: 'none',
                                                            background: '#fee2e2', color: '#dc2626',
                                                            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                                        }}>반려</button>
                                                    </>
                                                )}
                                                {org.isApproved === 'Y' && (
                                                    <button onClick={() => handleCancel('cancel', org.organizationCode)} style={{
                                                        padding: '4px 10px', borderRadius: 6, border: 'none',
                                                        background: hexToRgba(PURPLE, .10), color: PURPLE,
                                                        fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                                    }}>승인취소</button>
                                                )}
                                                {org.isApproved === 'R' && (
                                                    <>
                                                        <button onClick={() => handleDelete(org.organizationCode)} style={{
                                                            padding: '4px 10px', borderRadius: 6, border: 'none',
                                                            background: 'rgba(196,69,105,.10)', color: '#c44569',
                                                            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                                        }}>삭제</button>
                                                        <button onClick={() => handleCancel('rollback', org.organizationCode)} style={{
                                                            padding: '4px 10px', borderRadius: 6, border: 'none',
                                                            background: hexToRgba(MINT, .12), color: '#0e7a6e',
                                                            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                                        }}>롤백</button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {currentItems.length === 0 && (
                                    <tr>
                                        <td colSpan={9} style={{ padding: '48px 20px', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>
                                            조건에 맞는 조직이 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* 페이지네이션 */}
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '14px 20px', borderTop: '1px solid #ececf2',
                        background: '#fafaff',
                    }}>
                        <span style={{ fontSize: 12, color: '#8b8a99' }}>전체 {filteredList.length}건</span>
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', gap: 4 }}>
                                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} style={pageBtn(currentPage === 1)}>«</button>
                                <button onClick={handlePrevGroup} disabled={currentPage === 1} style={pageBtn(currentPage === 1)}>‹</button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(n => n >= Math.max(1, currentPage - 2) && n <= Math.min(totalPages, currentPage + 2))
                                    .map(n => (
                                        <button key={n} onClick={() => handlePageChange(n)} style={pageBtn(false, n === currentPage)}>
                                            {n}
                                        </button>
                                    ))}
                                <button onClick={handleNextGroup} disabled={currentPage === totalPages} style={pageBtn(currentPage === totalPages)}>›</button>
                                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} style={pageBtn(currentPage === totalPages)}>»</button>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {showModal && (
                <DeleteOrganizationConfirmModal
                    organizationCode={organizationCodeToDelete}
                    onConfirm={() => handleForceDelete(organizationCodeToDelete)}
                    onClose={() => setShowModal(false)}
                    isOpen={showModal}
                />
            )}

            <OrganizationInfoModal
                isOpen={showOrgInfoModal}
                onClose={() => setShowOrgInfoModal(false)}
                organizationData={selectedOrgData}
            />
        </div>
    );
}
